/*
  assets/ls-product-card.js
  LOST SIGNAL — Luxury Product Card behaviour
  Paired with snippets/product-card.liquid. Load once per section:
  <script src="{{ 'ls-product-card.js' | asset_url }}" defer="defer"></script>

  Handles: scroll reveal, colour swatch image swap, quick add (single and
  multi-variant), cart count refresh, and keyboard dismissal.
  Safe to load more than once — initialisation is guarded per card.
*/

(function () {
  'use strict';

  if (window.LSProductCard) return;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function routeFor(path) {
    var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    return root + path;
  }

  function announce(message) {
    var region = document.getElementById('ls-cart-status');

    if (!region) {
      region = document.createElement('p');
      region.id = 'ls-cart-status';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      region.style.cssText =
        'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap;';
      document.body.appendChild(region);
    }

    region.textContent = message;
  }

  function refreshCartCount() {
    fetch(routeFor('cart.js'), { headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        document.querySelectorAll('.ls-header__cart-count').forEach(function (el) {
          el.textContent = cart.item_count;
          el.setAttribute('data-count', cart.item_count);
        });

        document.dispatchEvent(
          new CustomEvent('ls:cart:updated', { bubbles: true, detail: { cart: cart } })
        );
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      })
      .catch(function () {
        /* The header count stays as-is rather than showing a wrong number. */
      });
  }

  function closeAllPickers(except) {
    document.querySelectorAll('.ls-pcard__picker').forEach(function (picker) {
      if (picker === except) return;
      picker.classList.remove('is-open');
      picker.hidden = true;
    });
  }

  function addToCart(variantId, button, card) {
    var label = button.querySelector('[data-quick-label]');
    var defaultLabel = card.dataset.quickLabel || 'Quick Add';

    button.classList.add('is-loading');
    button.disabled = true;
    if (label) label.textContent = '···';

    fetch(routeFor('cart/add.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('add_failed');
        return res.json();
      })
      .then(function (data) {
        button.classList.remove('is-loading');
        if (label) label.textContent = 'Added';
        announce((data.title || 'Item') + ' added to cart');
        refreshCartCount();

        window.setTimeout(function () {
          button.disabled = false;
          if (label) label.textContent = defaultLabel;
        }, 2200);
      })
      .catch(function () {
        button.classList.remove('is-loading');
        button.disabled = false;
        if (label) label.textContent = defaultLabel;
        announce('Could not add to cart. Open the product page to continue.');
      });
  }

  function initCard(card) {
    if (card.dataset.lsInit === 'true') return;
    card.dataset.lsInit = 'true';

    var picker = card.querySelector('[data-picker]');
    var sizesWrap = card.querySelector('[data-sizes]');
    var quickBtn = card.querySelector('[data-quick-add]');
    var variantsEl = card.querySelector('[data-variants]');
    var mapEl = card.querySelector('[data-option-map]');
    var primaryImg = card.querySelector('[data-primary-image]');

    var variants = [];
    var optionMap = { color: null, size: null };

    try {
      if (variantsEl) variants = JSON.parse(variantsEl.textContent);
      if (mapEl) optionMap = JSON.parse(mapEl.textContent);
    } catch (err) {
      variants = [];
    }

    var selectedColor = null;

    /* ----- Colour swatches: swap the image, scope the size list ----- */
    card.querySelectorAll('[data-swatch]').forEach(function (swatch) {
      swatch.addEventListener('click', function () {
        var nextImage = swatch.getAttribute('data-swatch-image');
        selectedColor = swatch.getAttribute('data-swatch-value');

        card.querySelectorAll('[data-swatch]').forEach(function (other) {
          other.setAttribute('aria-pressed', other === swatch ? 'true' : 'false');
        });

        if (nextImage && primaryImg) {
          primaryImg.style.opacity = '0';
          window.setTimeout(function () {
            primaryImg.removeAttribute('srcset');
            primaryImg.setAttribute('src', nextImage);
            primaryImg.style.opacity = '';
          }, 180);
        }

        if (picker && picker.classList.contains('is-open')) buildSizes();
      });
    });

    /* ----- Build the size row for the currently selected colour ----- */
    function buildSizes() {
      if (!sizesWrap) return;
      sizesWrap.innerHTML = '';

      var pool = variants;

      if (selectedColor !== null && optionMap.color !== null) {
        var scoped = variants.filter(function (variant) {
          return variant.options[optionMap.color] === selectedColor;
        });
        if (scoped.length) pool = scoped;
      }

      pool.forEach(function (variant) {
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'ls-pcard__size';

        var label = variant.title;
        if (optionMap.size !== null && variant.options[optionMap.size]) {
          label = variant.options[optionMap.size];
        }

        button.textContent = label;
        button.disabled = !variant.available;
        button.setAttribute('aria-label', label + (variant.available ? '' : ' — sold out'));

        button.addEventListener('click', function () {
          picker.classList.remove('is-open');
          picker.hidden = true;
          addToCart(variant.id, quickBtn, card);
        });

        sizesWrap.appendChild(button);
      });
    }

    /* ----- Quick add ----- */
    if (quickBtn) {
      quickBtn.addEventListener('click', function () {
        var single = quickBtn.getAttribute('data-single') === 'true';

        if (single || !picker) {
          addToCart(parseInt(quickBtn.getAttribute('data-variant-id'), 10), quickBtn, card);
          return;
        }

        closeAllPickers(picker);

        if (picker.classList.contains('is-open')) {
          picker.classList.remove('is-open');
          picker.hidden = true;
          return;
        }

        picker.hidden = false;
        buildSizes();

        window.requestAnimationFrame(function () {
          picker.classList.add('is-open');
          var firstAvailable = picker.querySelector('.ls-pcard__size:not([disabled])');
          if (firstAvailable) firstAvailable.focus();
        });
      });
    }

    /* ----- Dismiss the picker when focus leaves the card ----- */
    card.addEventListener('focusout', function (event) {
      if (picker && !card.contains(event.relatedTarget)) {
        picker.classList.remove('is-open');
        picker.hidden = true;
      }
    });
  }

  /* ---------- Scroll reveal ---------- */
  var revealObserver = null;

  if (!REDUCED && 'IntersectionObserver' in window) {
    revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          revealObserver.unobserve(entry.target);
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -6% 0px' }
    );
  }

  function initAll(scope) {
    var root = scope || document;

    root.querySelectorAll('[data-ls-pcard]').forEach(function (card) {
      initCard(card);

      if (revealObserver) {
        revealObserver.observe(card);
      } else {
        card.classList.add('is-revealed');
      }
    });
  }

  /* ---------- Global listeners ---------- */
  document.addEventListener('click', function (event) {
    if (!event.target.closest('.ls-pcard')) closeAllPickers(null);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeAllPickers(null);
  });

  /* Theme editor re-renders sections without a page load. */
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
    event.target.querySelectorAll('[data-ls-pcard]').forEach(function (card) {
      card.classList.add('is-revealed');
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  window.LSProductCard = { init: initAll, refreshCartCount: refreshCartCount };
})();